import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  Heart,
  Star,
  Users,
  ArrowRight,
  MessageCircle,
  Calendar,
  Globe
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: 'general',
    message: '',
    travelDates: '',
    budget: '',
    destination: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    // Here you would handle the form submission
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Téléphone Prioritaire",
      subtitle: "Disponible 7j/7",
      info: "+1 786 840 0745",
      description: "Parlez directement à un conseiller expert PRIVIA",
      hours: "Lun-Sam 7:30-19:30 EST",
      action: "Appeler maintenant"
    },
    {
      icon: Mail,
      title: "Email Premium",
      subtitle: "Réponse sous 2h",
      info: "chris@priviacollection.com",
      description: "Pour toute demande détaillée ou personnalisée",
      hours: "Réponse garantie sous 2 heures",
      action: "Envoyer un email"
    },
    {
      icon: MessageCircle,
      title: "Chat Concierge",
      subtitle: "Service immédiat",
      info: "Chat en direct",
      description: "Support instantané pour vos questions urgentes",
      hours: "24h/24 - 7j/7",
      action: "Démarrer le chat"
    }
  ];

  const offices = [
    {
      city: "Miami",
      country: "États-Unis - Siège",
      address: "1001 Brickell Bay Dr, Suite 2700\nMiami, FL 33131",
      phone: "+1 786 840 0745",
      email: "miami@priviacollection.com",
      hours: "Lun-Ven 9:00-18:00 EST"
    },
    {
      city: "Monaco",
      country: "Europe",
      address: "7 Avenue de Grande Bretagne\n98000 Monaco",
      phone: "+377 97 70 00 00",
      email: "monaco@priviacollection.com",
      hours: "Lun-Ven 9:00-18:00 CET"
    },
    {
      city: "Saint-Barthélemy",
      country: "Caraïbes",
      address: "Rue de la République\n97133 Saint-Barthélemy",
      phone: "+590 590 87 00 00",
      email: "stbarth@priviacollection.com",
      hours: "Lun-Sam 8:00-17:00 AST"
    }
  ];

  const subjectOptions = [
    { value: 'general', label: 'Information générale' },
    { value: 'booking', label: 'Nouvelle réservation' },
    { value: 'existing', label: 'Réservation existante' },
    { value: 'concierge', label: 'Services concierge' },
    { value: 'partnership', label: 'Partenariat' },
    { value: 'press', label: 'Presse & Médias' },
    { value: 'other', label: 'Autre' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1600607687644-c7171b42498b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Heart className="w-4 h-4 mr-2" />
            Contact Premium
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Parlons de votre Rêve
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nos experts sont là pour transformer vos envies d'évasion en expériences d'exception personnalisées
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Comment nous Joindre
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Choisissez le canal qui vous convient le mieux pour une réponse personnalisée
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              return (
                <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 
                    className="text-xl font-light text-gray-800 mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {method.title}
                  </h3>
                  
                  <p className="text-amber-600 font-medium mb-4 text-sm uppercase tracking-wide">
                    {method.subtitle}
                  </p>
                  
                  <p className="text-2xl font-light text-gray-800 mb-4">
                    {method.info}
                  </p>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                    {method.description}
                  </p>
                  
                  <p className="text-xs text-gray-500 mb-6">
                    {method.hours}
                  </p>
                  
                  <Button className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-2 rounded-full font-medium transition-all duration-300">
                    {method.action}
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Offices */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Form */}
            <div>
              <h3 
                className="text-3xl font-light mb-8 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Dites-nous tout
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet de votre demande *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    required
                  >
                    {subjectOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dates de voyage souhaitées
                    </label>
                    <input
                      type="text"
                      name="travelDates"
                      value={formData.travelDates}
                      onChange={handleInputChange}
                      placeholder="Ex: Juillet 2024"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget approximatif
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="">Sélectionnez votre budget</option>
                      <option value="20-30k">20 000 - 30 000€</option>
                      <option value="30-50k">30 000 - 50 000€</option>
                      <option value="50-75k">50 000 - 75 000€</option>
                      <option value="75k+">75 000€ et plus</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    placeholder="Décrivez-nous votre projet de voyage idéal..."
                    required
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-3 rounded-lg font-medium text-lg transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Envoyer ma Demande
                  <Send className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </div>

            {/* Offices */}
            <div>
              <h3 
                className="text-3xl font-light mb-8 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Nos Bureaux
              </h3>
              
              <div className="space-y-8">
                {offices.map((office, index) => (
                  <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-amber-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 
                            className="text-xl font-light text-gray-800"
                            style={{ fontFamily: 'Playfair Display, serif' }}
                          >
                            {office.city}
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {office.country}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div className="whitespace-pre-line">{office.address}</div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            <span>{office.phone}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span>{office.email}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span>{office.hours}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Response Promise */}
              <Card className="mt-8 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
                <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-600" />
                  Notre Promesse
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full" />
                    Réponse personnalisée sous 2 heures
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full" />
                    Conseiller dédié pour votre projet
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-600 rounded-full" />
                    Proposition sur-mesure gratuite
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;